/**
 * Hasta Rekhā — the reference set: the lines, the mounts, and what the tradition
 * says each stands for.
 *
 * ◆ **Descriptive, never predictive.** Each entry says what the tradition
 * associates a line or mount with, in the language of temperament and theme. None
 * of it pronounces on lifespan, health, fertility, or another person — the
 * boundaries in common/docs/PALMISTRY.md § Boundaries. The Jīvana Rekhā is read as
 * vitality and constitution, the Budha Rekhā as communication and commerce.
 *
 * ⚠ These strings are checked by tests/hasta/reference.test.js against the same
 * forbidden-vocabulary linter that guards the yoga rules, so a sentence that
 * crosses the line fails the build rather than reaching a page.
 *
 * The overlay positions are a **reference frame for the reader to align by
 * eye**, in a unit square with the fingers at the top. They are not detections.
 * Nothing in this file measures a photograph.
 *
 * © Manish Jagdish Thatte
 */

/** The seven principal rekhās. `colour` names the CSS custom property in tokens.css. */
export const LINES = Object.freeze([
  {
    id: 'hridaya', sa: 'हृदय रेखा', name: 'Hṛdaya Rekhā', english: 'Heart line', colour: '--rekha-hridaya', fallback: '#c2410c',
    where: 'The upper transverse line, running in from the edge of the palm below the little finger.',
    tradition: 'The emotional life: how feeling is felt and shown, warmth and reserve in attachment.'
  },
  {
    id: 'mastishka', sa: 'मस्तिष्क रेखा', name: 'Mastiṣka Rekhā', english: 'Head line', colour: '--rekha-mastishka', fallback: '#1d4ed8',
    where: 'The middle transverse line, starting between the thumb and the index finger.',
    tradition: 'The manner of the mind: practical or imaginative, focused or wide-ranging, quick or deliberate.'
  },
  {
    id: 'jivana', sa: 'जीवन रेखा', name: 'Jīvana Rekhā', english: 'Life line', colour: '--rekha-jivana', fallback: '#15803d',
    where: 'The arc that encloses the mount of Śukra at the base of the thumb.',
    tradition: 'Vitality and constitution — the quality and steadiness of energy. The tradition does not read its length as a measure of years.'
  },
  {
    id: 'bhagya', sa: 'भाग्य रेखा', name: 'Bhāgya Rekhā', english: 'Fate line', colour: '--rekha-bhagya', fallback: '#7c3aed',
    where: 'The vertical line rising from the wrist toward the mount of Śani, when present.',
    tradition: 'The sense of direction in one’s work and duties, and how much circumstance shapes the path. Many hands do not have it.'
  },
  {
    id: 'surya', sa: 'सूर्य रेखा', name: 'Sūrya Rekhā', english: 'Sun line', colour: '--rekha-surya', fallback: '#b8860b',
    where: 'A vertical line toward the ring finger, when present.',
    tradition: 'Recognition, creative satisfaction and the enjoyment of one’s work.'
  },
  {
    id: 'budha', sa: 'बुध रेखा', name: 'Budha Rekhā', english: 'Mercury line', colour: '--rekha-budha', fallback: '#0891b2',
    where: 'A vertical line toward the little finger, when present.',
    tradition: 'Communication and commerce — speech, writing and dealing with others.'
  },
  {
    id: 'vivaha', sa: 'विवाह रेखा', name: 'Vivāha Rekhā', english: 'Relationship lines', colour: '--rekha-vivaha', fallback: '#be185d',
    where: 'Short horizontal lines on the edge of the palm below the little finger.',
    tradition: 'Close partnership as a theme in the life of the person whose hand it is. It says nothing about anyone else.'
  }
]);

/**
 * The mounts, each with the graha the tradition assigns it. `x`, `y` are the
 * centre in a unit square for a **left palm photographed palm-up, thumb on the
 * left, fingers at the top**; mirror `x` for a right palm.
 */
export const MOUNTS = Object.freeze([
  { id: 'guru', graha: 'jupiter', name: 'Guru', english: 'Jupiter', x: 0.29, y: 0.30, tradition: 'Aspiration, leadership and the wish to teach or guide.' },
  { id: 'shani', graha: 'saturn', name: 'Śani', english: 'Saturn', x: 0.46, y: 0.28, tradition: 'Seriousness, self-reliance and a taste for solitude and order.' },
  { id: 'surya', graha: 'sun', name: 'Sūrya', english: 'Sun', x: 0.61, y: 0.30, tradition: 'Brightness, creativity and the appetite for recognition.' },
  { id: 'budha', graha: 'mercury', name: 'Budha', english: 'Mercury', x: 0.75, y: 0.33, tradition: 'Wit, speech and aptitude for trade and exchange.' },
  { id: 'mangala-upper', graha: 'mars', name: 'Maṅgala (upper)', english: 'Mars, upper', x: 0.80, y: 0.50, tradition: 'Steadiness under pressure — moral courage and resistance.' },
  { id: 'mangala-lower', graha: 'mars', name: 'Maṅgala (lower)', english: 'Mars, lower', x: 0.25, y: 0.49, tradition: 'Initiative and physical courage.' },
  { id: 'chandra', graha: 'moon', name: 'Candra', english: 'Moon', x: 0.76, y: 0.74, tradition: 'Imagination, intuition and memory.' },
  { id: 'shukra', graha: 'venus', name: 'Śukra', english: 'Venus', x: 0.30, y: 0.72, tradition: 'Warmth, affection and the enjoyment of beauty and company.' }
]);

/** Mount centres for a left or right palm. */
export function mountPositions(hand = 'left') {
  return MOUNTS.map((m) => ({ ...m, x: hand === 'right' ? 1 - m.x : m.x }));
}

export function lineById(id) {
  const l = LINES.find((x) => x.id === id);
  if (!l) throw new RangeError(`unknown rekhā "${id}"`);
  return l;
}

export const PALM_BOUNDARY_NOTE =
  'This page describes what the tradition associates with each line and mount. It is not a diagnosis, ' +
  'and it says nothing about length of life, health, fertility, or any person other than the one whose ' +
  'hand it is.';
